import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const CourierStatus = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  const [statuses, setStatuses] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    CourierStatusID: null,
    StatusName: '',
    StatusColor: '#000000',
    StatusOrder: 0,
    IsActive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    filterStatuses();
  }, [searchTerm, statuses]);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/CourierStatus.php');
      if (response.data.success) {
        setStatuses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching courier statuses:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterStatuses = () => {
    if (!searchTerm.trim()) {
      setFilteredStatuses(statuses);
    } else {
      const filtered = statuses.filter(status =>
        status.StatusName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStatuses(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.StatusName.trim()) {
      newErrors.StatusName = 'Status name is required';
    } else if (formData.StatusName.length > 50) {
      newErrors.StatusName = 'Status name must be 50 characters or less';
    }

    if (!formData.StatusColor || !/^#[0-9A-Fa-f]{6}$/.test(formData.StatusColor)) {
      newErrors.StatusColor = 'A valid color is required';
    }

    if (formData.StatusOrder < 0 || isNaN(formData.StatusOrder)) {
      newErrors.StatusOrder = 'Order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const payload = {
        ...formData,
        IsActive: formData.IsActive ? 1 : 0,
        StatusOrder: parseInt(formData.StatusOrder, 10)
      };

      const response = await apiClient[method]('/CourierStatus.php', payload);
      const data = response.data;

      if (data.success) {
        alert(editMode ? 'Courier status updated successfully!' : 'Courier status created successfully!');
        setShowModal(false);
        resetForm();
        fetchStatuses();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving courier status:', error);
      if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error saving courier status. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (status) => {
    setFormData({
      CourierStatusID: status.CourierStatusID,
      StatusName: status.StatusName,
      StatusColor: status.StatusColor || '#000000',
      StatusOrder: status.StatusOrder || 0,
      IsActive: status.IsActive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this courier status?')) return;

    try {
      const response = await apiClient.delete('/CourierStatus.php', {
        data: { CourierStatusID: id }
      });

      if (response.data.success) {
        alert('Courier status deleted successfully!');
        fetchStatuses();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting courier status:', error);
      if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error deleting courier status. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      CourierStatusID: null,
      StatusName: '',
      StatusColor: '#000000',
      StatusOrder: 0,
      IsActive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Helper: get a readable contrast color (black or white) for text on the status color bg
  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Courier Status Master</h2>
          <p className="subtitle">Manage courier delivery statuses</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Status
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by status name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredStatuses.length} {filteredStatuses.length === 1 ? 'status' : 'statuses'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading courier statuses...</div>
      ) : filteredStatuses.length === 0 ? (
        <div className="empty-state">
          <p>No courier statuses found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Status Name</th>
                <th>Color</th>
                <th>Active</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStatuses.map((status) => (
                <tr key={status.CourierStatusID}>
                  <td className="font-mono" style={{ textAlign: 'center' }}>{status.StatusOrder}</td>
                  <td className="font-semibold">{status.StatusName}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          backgroundColor: status.StatusColor || '#000000',
                          border: '2px solid #e5e7eb',
                        }}
                      />
                      <span className="font-mono" style={{ fontSize: '12px' }}>{status.StatusColor}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${status.IsActive ? 'active' : 'inactive'}`}>
                      {status.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{status.CreatedAt ? new Date(status.CreatedAt).toLocaleDateString() : '-'}</td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(status)} title="Edit">
                      <Edit2 size={16} />
                    </button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(status.CourierStatusID)} title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Courier Status' : 'Add New Courier Status'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Status Name */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="StatusName">
                    Status Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="StatusName"
                    value={formData.StatusName}
                    onChange={(e) => setFormData({ ...formData, StatusName: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., In Transit, Delivered, Pending"
                    className={errors.StatusName ? 'error' : ''}
                  />
                  {errors.StatusName && <span className="error-message">{errors.StatusName}</span>}
                </div>

                {/* Display Order */}
                <div className="form-group">
                  <label htmlFor="StatusOrder">
                    Display Order <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="StatusOrder"
                    value={formData.StatusOrder}
                    onChange={(e) => setFormData({ ...formData, StatusOrder: parseInt(e.target.value, 10) || 0 })}
                    min="0"
                    placeholder="0"
                    className={errors.StatusOrder ? 'error' : ''}
                  />
                  {errors.StatusOrder && <span className="error-message">{errors.StatusOrder}</span>}
                </div>
              </div>

              {/* Color Picker Row */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="StatusColor">
                    Status Color <span className="required">*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="color"
                      id="StatusColor"
                      value={formData.StatusColor}
                      onChange={(e) => setFormData({ ...formData, StatusColor: e.target.value.toUpperCase() })}
                      style={{
                        width: '48px',
                        height: '42px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '2px',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    />
                    <input
                      type="text"
                      value={formData.StatusColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, StatusColor: val });
                      }}
                      maxLength="7"
                      placeholder="#000000"
                      className={errors.StatusColor ? 'error' : ''}
                      style={{ flex: 1 }}
                    />
                  </div>
                  {errors.StatusColor && <span className="error-message">{errors.StatusColor}</span>}
                </div>

                {/* Live Preview */}
                <div className="form-group">
                  <label>Preview</label>
                  <div style={{ display: 'flex', alignItems: 'center', height: '42px' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px 18px',
                        borderRadius: '20px',
                        backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(formData.StatusColor) ? formData.StatusColor : '#000000',
                        color: /^#[0-9A-Fa-f]{6}$/.test(formData.StatusColor) ? getContrastColor(formData.StatusColor) : '#ffffff',
                        fontSize: '13px',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        border: '2px solid rgba(0,0,0,0.1)',
                      }}
                    >
                      {formData.StatusName.trim() || 'Status Name'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Active Checkbox */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.IsActive}
                    onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierStatus;